// Minimal accessible modal/sheet. Slides up from bottom on mobile, centers on desktop.
// No Radix dep — handles focus trap loosely (Esc to close, click-out, primary CTA inside).

import { useEffect, type ReactNode } from "react";
import { cn } from "../../lib/cn";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Sheet({ open, onClose, title, description, children, footer }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex sm:items-center sm:justify-center items-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "sheet-title" : undefined}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
      />
      <div
        className={cn(
          "relative bg-surface shadow-lg border border-border",
          "w-full sm:max-w-lg",
          "rounded-t-xl sm:rounded-lg",
          "max-h-[92dvh] sm:max-h-[85dvh] flex flex-col",
        )}
      >
        {(title || description) && (
          <div className="px-5 pt-5 pb-3 border-b border-border">
            {title && (
              <h2 id="sheet-title" className="text-lg font-semibold tracking-tight">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-1 text-sm text-text-muted">{description}</p>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-border bg-surface-2 rounded-b-xl sm:rounded-b-lg">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
